import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Headphones, Clock, Sparkles, Plus, Upload, Play, Pause, Trash2, MoreVertical, Video, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import BinauralDeep from '@/components/BinauralDeep';
import { MeditationsSkeleton } from '@/components/Skeletons';

const CATEGORIES = ['reset', 'core', 'deep', 'recode', 'seat'];
const CATEGORY_LABELS = {
  reset: 'Reset',
  core: 'Center',
  deep: 'Deep',
  recode: 'Release',
  seat: 'Identity',
};
const CATEGORY_COLORS = {
  reset: 'bg-moss/15 text-moss border-moss/20',
  core: 'bg-gold/15 text-gold border-gold/20',
  deep: 'bg-dark/15 text-dark border-dark/20',
  recode: 'bg-gold/25 text-dark border-gold/30',
  seat: 'bg-gold/15 text-gold border-gold/20',
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getSessionToken() {
  const match = document.cookie.match(/session_token=([^;]+)/);
  return match ? match[1] : '';
}

function getFileUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BACKEND_URL}/api/files/${path}?auth=${getSessionToken()}`;
}

function AudioPlayer({ src }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggle = () => {
    if (!audioRef.current) return;
    setError(null);
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    // Pause any other audio currently playing so meditations don't overlap.
    document.querySelectorAll('audio').forEach((a) => {
      if (a !== audioRef.current && !a.paused) a.pause();
    });
    const p = audioRef.current.play();
    if (p && p.catch) {
      p.catch((err) => {
        setError(err?.message || 'Could not play');
        setPlaying(false);
        setLoading(false);
      });
    }
    setPlaying(true);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => { setProgress(audio.currentTime); };
    const onMeta = () => { setDuration(audio.duration); };
    const onEnd = () => { setPlaying(false); setProgress(0); };
    const onWait = () => { setLoading(true); };
    const onPlaying = () => { setLoading(false); };
    const onErr = () => {
      const code = audio.error?.code;
      const msg = code === 4 ? 'Audio format not supported here'
                : code === 2 ? 'Network error while loading audio'
                : code === 3 ? 'Audio is corrupted'
                : 'Could not load audio';
      setError(msg);
      setPlaying(false);
      setLoading(false);
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('waiting', onWait);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('canplay', onPlaying);
    audio.addEventListener('error', onErr);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('waiting', onWait);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('canplay', onPlaying);
      audio.removeEventListener('error', onErr);
    };
  }, []);

  const pct = duration > 0 ? (progress / duration) * 100 : 0;
  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  return (
    <div className="flex items-center gap-3 mt-4" data-testid="audio-player">
      <audio ref={audioRef} src={src} preload="metadata" />
      <button onClick={toggle} data-testid="audio-play-button" className="w-10 h-10 rounded-full bg-dark/80 hover:bg-dark text-bone flex items-center justify-center shrink-0 btn-hover" disabled={!!error}>
        {loading && playing ? (
          <span className="inline-block w-3 h-3 border-2 border-bone border-t-transparent rounded-full animate-spin" />
        ) : playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
      </button>
      <div className="flex-1">
        {error ? (
          <p className="font-sans text-[11px] text-red-600" data-testid="audio-error">{error}</p>
        ) : (
          <>
            <div className="h-1.5 bg-sand rounded-full overflow-hidden cursor-pointer" onClick={(e) => {
              if (!audioRef.current || !duration) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const pctClick = (e.clientX - rect.left) / rect.width;
              audioRef.current.currentTime = pctClick * duration;
            }}>
              <div className="h-full bg-gold rounded-full" style={{ width: `${pct}%`, transition: 'width 0.1s linear' }} />
            </div>
            <div className="flex justify-between mt-1">
              <span className="font-sans text-[10px] text-text-muted">
                {loading && playing ? 'Loading…' : fmt(progress)}
              </span>
              <span className="font-sans text-[10px] text-text-muted">{fmt(duration)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function MeditationsPage() {
  const [meditations, setMeditations] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedMed, setExpandedMed] = useState(null);
  const [expandedScript, setExpandedScript] = useState(null);
  const [loading, setLoading] = useState(true);

  // Upload form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('');
  const [category, setCategory] = useState('reset');
  const [audioFile, setAudioFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadMeditations(); }, []);

  const loadMeditations = async () => {
    try {
      const res = await api.get('/meditations');
      setMeditations(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const uploadFileToStorage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data.path;
  };

  const createMeditation = async () => {
    if (!title.trim() || !duration) return;
    setUploading(true);
    try {
      let audioPath = null, thumbnailPath = null, videoPath = null;

      if (audioFile) audioPath = await uploadFileToStorage(audioFile);
      if (thumbnailFile) thumbnailPath = await uploadFileToStorage(thumbnailFile);
      if (videoFile) videoPath = await uploadFileToStorage(videoFile);

      const res = await api.post('/meditations', {
        title: title.trim(),
        description: description.trim(),
        duration: parseInt(duration),
        category,
        audio_path: audioPath,
        thumbnail_path: thumbnailPath,
        video_path: videoPath,
      });

      setMeditations(prev => [res.data, ...prev]);
      resetForm();
      setDialogOpen(false);
      toast.success('Meditation added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const deleteMeditation = async (medId) => {
    try {
      await api.delete(`/meditations/${medId}`);
      setMeditations(prev => prev.filter(m => m.meditation_id !== medId));
      toast.success('Meditation removed');
    } catch (err) { toast.error('Could not delete'); }
  };

  const resetForm = () => {
    setTitle(''); setDescription(''); setDuration(''); setCategory('reset');
    setAudioFile(null); setThumbnailFile(null); setVideoFile(null);
  };

  const categories = CATEGORIES; // Always show all SAP stages
  const filtered = selectedCategory ? meditations.filter(m => m.category === selectedCategory) : meditations;

  if (loading) return <MeditationsSkeleton />;

  return (
    <div className="space-y-8" data-testid="meditations-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 animate-fade-up">
        <div>
          <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-2">Guided Practices</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-dark tracking-tight mystical-heading">Meditations</h1>
          <p className="text-text-muted font-sans text-base mt-3 max-w-xl">
            These practices are designed to help you embody the identity of your highest self.
          </p>
          <div className="mystical-divider mt-4" />
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="upload-meditation-button" className="bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider btn-hover">
              <Plus size={16} className="mr-2" /> Upload Meditation
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-bone border-gold/15 rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="upload-meditation-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-dark">Add Guided Practice</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-text-muted text-xs tracking-wider uppercase font-sans">Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Morning Identity Embodiment" data-testid="meditation-title-input" className="h-11 mt-1 bg-sand/50 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans" />
              </div>
              <div>
                <Label className="text-text-muted text-xs tracking-wider uppercase font-sans">Description</Label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="A gentle practice to..." data-testid="meditation-desc-input" className="min-h-[80px] mt-1 bg-sand/50 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-text-muted text-xs tracking-wider uppercase font-sans">Duration (min)</Label>
                  <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="15" data-testid="meditation-duration-input" className="h-11 mt-1 bg-sand/50 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans" />
                </div>
                <div>
                  <Label className="text-text-muted text-xs tracking-wider uppercase font-sans">Category</Label>
                  <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="meditation-category-select" className="w-full h-11 mt-1 bg-sand/50 border border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans text-sm px-3">
                    {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-text-muted text-xs tracking-wider uppercase font-sans flex items-center gap-1.5"><Headphones size={12} /> Audio File</Label>
                  <label data-testid="meditation-audio-upload" className="mt-1 flex items-center gap-3 p-3 rounded-xl border border-dashed border-black/15 hover:border-gold/30 cursor-pointer" style={{ transition: 'border-color 0.2s ease' }}>
                    <Upload size={16} className="text-text-muted" />
                    <span className="font-sans text-sm text-text-muted">{audioFile ? audioFile.name : 'Choose audio file (MP3, WAV...)'}</span>
                    <input type="file" accept="audio/*" onChange={(e) => setAudioFile(e.target.files?.[0] || null)} className="hidden" />
                  </label>
                </div>
                <div>
                  <Label className="text-text-muted text-xs tracking-wider uppercase font-sans flex items-center gap-1.5"><ImageIcon size={12} /> Thumbnail Image</Label>
                  <label data-testid="meditation-thumbnail-upload" className="mt-1 flex items-center gap-3 p-3 rounded-xl border border-dashed border-black/15 hover:border-gold/30 cursor-pointer" style={{ transition: 'border-color 0.2s ease' }}>
                    <Upload size={16} className="text-text-muted" />
                    <span className="font-sans text-sm text-text-muted">{thumbnailFile ? thumbnailFile.name : 'Choose thumbnail (JPG, PNG...)'}</span>
                    <input type="file" accept="image/*" onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)} className="hidden" />
                  </label>
                </div>
                <div>
                  <Label className="text-text-muted text-xs tracking-wider uppercase font-sans flex items-center gap-1.5"><Video size={12} /> Video File (optional)</Label>
                  <label data-testid="meditation-video-upload" className="mt-1 flex items-center gap-3 p-3 rounded-xl border border-dashed border-black/15 hover:border-gold/30 cursor-pointer" style={{ transition: 'border-color 0.2s ease' }}>
                    <Upload size={16} className="text-text-muted" />
                    <span className="font-sans text-sm text-text-muted">{videoFile ? videoFile.name : 'Choose video (MP4, MOV...)'}</span>
                    <input type="file" accept="video/*" onChange={(e) => setVideoFile(e.target.files?.[0] || null)} className="hidden" />
                  </label>
                </div>
              </div>
              <Button onClick={createMeditation} disabled={!title.trim() || !duration || uploading} data-testid="save-meditation-button" className="w-full bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider btn-hover">
                {uploading ? 'Uploading...' : 'Add Meditation'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-2 flex-wrap animate-fade-up delay-100" data-testid="meditation-category-filter">
        <button onClick={() => setSelectedCategory(null)} data-testid="filter-all" className={`px-4 py-2 rounded-full text-xs font-sans tracking-wider uppercase ${!selectedCategory ? 'bg-dark text-bone' : 'bg-bone/60 text-text-muted hover:bg-bone'}`} style={{ transition: 'background-color 0.2s ease, color 0.2s ease' }}>All</button>
        {categories.map((cat) => (
          <button key={cat} onClick={() => setSelectedCategory(cat === selectedCategory ? null : cat)} data-testid={`filter-${cat}`} className={`px-4 py-2 rounded-full text-xs font-sans tracking-wider uppercase ${selectedCategory === cat ? 'bg-dark text-bone' : 'bg-bone/60 text-text-muted hover:bg-bone'}`} style={{ transition: 'background-color 0.2s ease, color 0.2s ease' }}>
            {CATEGORY_LABELS[cat] || cat}
          </button>
        ))}
      </div>

      {/* Stage 3 — The Deep (synthesized binaural) */}
      {(!selectedCategory || selectedCategory === 'deep') && <BinauralDeep />}

      {/* Meditation Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((med, i) => {
          const thumbUrl = getFileUrl(med.thumbnail_path) || med.image_url;
          const audioUrl = getFileUrl(med.audio_path);
          const videoUrl = getFileUrl(med.video_path);
          const isExpanded = expandedMed === med.meditation_id;

          return (
            <div key={med.meditation_id} className="mystical-card overflow-hidden group animate-fade-up" style={{ animationDelay: `${i * 0.08}s` }} data-testid={`meditation-card-${med.meditation_id}`}>
              {/* Thumbnail / Video */}
              <div className="relative h-48 overflow-hidden cursor-pointer" onClick={() => setExpandedMed(isExpanded ? null : med.meditation_id)}>
                {videoUrl && isExpanded ? (
                  <video src={videoUrl} controls className="w-full h-full object-cover" data-testid={`meditation-video-${med.meditation_id}`} />
                ) : (
                  <>
                    {thumbUrl ? (
                      <img src={thumbUrl} alt={med.title} className="w-full h-full object-cover group-hover:scale-105" style={{ transition: 'transform 0.6s ease' }} />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gold/10 to-moss/10 flex items-center justify-center">
                        <Headphones size={40} className="text-gold/30" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-dark/50 to-transparent" />
                    {videoUrl && (
                      <div className="absolute top-3 left-3 flex items-center gap-1 bg-dark/60 text-bone px-2 py-1 rounded-full">
                        <Video size={12} /><span className="font-sans text-[10px]">Video</span>
                      </div>
                    )}
                  </>
                )}
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                  <Badge className={`${CATEGORY_COLORS[med.category] || 'bg-sand text-dark'} border font-sans text-xs`}>
                    {CATEGORY_LABELS[med.category] || med.category}
                  </Badge>
                  <div className="flex items-center gap-1 text-bone/90">
                    <Clock size={14} /><span className="font-sans text-xs">{med.duration} min</span>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <h3 className="font-serif text-xl text-dark mb-2">{med.title}</h3>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="text-text-muted hover:text-dark p-1 shrink-0" data-testid={`meditation-menu-${med.meditation_id}`}><MoreVertical size={16} /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-bone border-black/10 rounded-xl">
                      <DropdownMenuItem onClick={() => deleteMeditation(med.meditation_id)} className="text-red-500 focus:text-red-600 font-sans text-sm" data-testid={`delete-meditation-${med.meditation_id}`}>
                        <Trash2 size={14} className="mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <p className="text-text-muted font-sans text-sm leading-relaxed">{med.description}</p>
                {med.script ? (
                  <div className="mt-4">
                    <button
                      onClick={() => setExpandedScript(expandedScript === med.meditation_id ? null : med.meditation_id)}
                      data-testid={`meditation-script-toggle-${med.meditation_id}`}
                      className="text-gold font-sans text-[11px] tracking-[0.2em] uppercase hover:text-gold/70 inline-flex items-center gap-1"
                      style={{ transition: 'color 0.2s ease' }}
                    >
                      {expandedScript === med.meditation_id ? 'Hide Script' : 'Read Script'} <Sparkles size={11} />
                    </button>
                    {expandedScript === med.meditation_id && (
                      <div
                        className="mt-3 p-4 rounded-xl bg-sand/40 border border-gold/15 animate-fade-up"
                        data-testid={`meditation-script-${med.meditation_id}`}
                      >
                        <p className="font-serif text-sm text-dark leading-relaxed whitespace-pre-line">
                          {med.script}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p
                    className="mt-4 inline-flex items-center gap-1 text-text-muted/80 font-sans text-[11px] tracking-[0.2em] uppercase"
                    data-testid={`meditation-script-pending-${med.meditation_id}`}
                  >
                    Script coming soon <Sparkles size={11} className="text-gold" />
                  </p>
                )}
                {audioUrl ? (
                  <AudioPlayer src={audioUrl} />
                ) : (
                  <div className="mt-4 flex items-center gap-2 text-text-muted">
                    <Headphones size={16} />
                    <span className="font-sans text-xs tracking-wider uppercase">No audio uploaded</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 animate-fade-up">
          <Sparkles size={36} className="mx-auto text-text-muted/30 mb-4" />
          <p className="font-serif text-xl text-text-muted">No meditations in this category yet</p>
        </div>
      )}
    </div>
  );
}
